import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./LatestHappenings.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const PUBLIC_URL = process.env.PUBLIC_URL || "";

const eventConfigs = [
  { endpoint: "/vista-with-images", tag: "STUDENT ACTIVITY", title: "Student Activity", placeholder: "/Events/Vista3-scaled.jpg", link: "/life/vista" },
  { endpoint: "/ecell-with-images", tag: "E-CELL", title: "E-Cell", placeholder: "/Events/ecell.jpg", link: "/life/e-cell-ayan" },
  { endpoint: "/codingclub-with-images", tag: "CODING CLUB", title: "Coding Club", placeholder: "/Events/TIRA-ROBOT.jpeg", link: "/life/coding-club" },
  { endpoint: "/sportsday-with-images", tag: "SPORTS", title: "Sports Day", placeholder: "/Events/Sports-Day-2023.jpg", link: "/life/sportsday" },
  { endpoint: "/trekking-with-images", tag: "TREKKING", title: "Trekking", placeholder: "/Events/Trek2.jpg", link: "/life/trekking" },
  { endpoint: "/freshersfarewell-with-images", tag: "FRESHERS & FAREWELL", title: "Freshers & Farewell", placeholder: "/Events/farewell.jpg", link: "/life/fresher-farewell" },
];

const fallbackEvents = eventConfigs.map((config) => ({
  title: config.title,
  tag: config.tag,
  images: [],
  placeholder: config.placeholder,
  link: config.link,
}));

const CACHE_KEY = "latestHappeningsCache";
const CACHE_TTL_MS = 5 * 60 * 1000;

const getImageSrc = (img) => {
  if (!img) return null;
  if (typeof img === "string") {
    if (img.startsWith("http") || img.startsWith("data:")) return img;
    if (img.startsWith("/")) {
      const prefix = PUBLIC_URL.endsWith("/") ? PUBLIC_URL.slice(0, -1) : PUBLIC_URL;
      return prefix ? `${prefix}${img}` : img;
    }
  }
  return `data:image/jpeg;base64,${img}`;
};

const sanitizeImages = (activity) => {
  const images = [
    activity?.feature_image,
    activity?.image1,
    activity?.image2,
    activity?.image3,
    activity?.image4,
  ].filter(Boolean);

  return [...new Set(images)].map((img) => getImageSrc(img));
};

export const getLatestActivityFromPayload = (data) => {
  const activities = Object.values(data || {}).flat();

  if (!activities.length) return null;

  return activities.reduce((latest, activity) => {
    const currentDate = activity?.selected_date ? new Date(activity.selected_date) : null;
    const latestDate = latest?.selected_date ? new Date(latest.selected_date) : null;

    if (!currentDate || Number.isNaN(currentDate.getTime())) return latest;
    if (!latestDate || Number.isNaN(latestDate.getTime())) return activity;

    return currentDate > latestDate ? activity : latest;
  }, null);
};

const EventCard = ({ event }) => {
  const navigate = useNavigate();
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  const images = event.images && event.images.length > 0
    ? event.images
    : [event.placeholder || "/Events/image00038.jpeg"];

  const nextSlide = (e) => {
    e?.stopPropagation();
    setCurrentImgIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = (e) => {
    e?.stopPropagation();
    setCurrentImgIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleCardClick = () => {
    if (event.link) {
      navigate(event.link);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardClick();
    }
  };

  useEffect(() => {
    setCurrentImgIndex(0);

    if (images.length < 2) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setCurrentImgIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }, 2500);

    return () => clearInterval(intervalId);
  }, [images]);

  return (
    <div
      className="lh-event-card"
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      aria-label={`Open ${event.title} page`}
    >
      <div className="lh-card-tab">
        <span className="lh-tab-text">{event.tag}</span>
      </div>

      <div className="lh-card-body">
        <div className="lh-image-wrapper">
          {images.map((imgSrc, i) => (
            <img
              key={`${event.title}-${i}`}
              src={imgSrc}
              alt={`${event.title} - ${i + 1}`}
              className={`lh-event-image ${i === currentImgIndex ? "active" : ""}`}
              loading="lazy"
              decoding="async"
            />
          ))}

          {images.length === 0 && (
            <div className="lh-empty-state">No images available from the source yet.</div>
          )}

      

          {images.length > 1 && (
            <>
              <button className="lh-carousel-btn left" onClick={prevSlide} aria-label="Previous image">
                &#10094;
              </button>
              <button className="lh-carousel-btn right" onClick={nextSlide} aria-label="Next image">
                &#10095;
              </button>

              <div className="lh-carousel-dots">
                {images.map((_, i) => (
                  <span
                    key={`${event.title}-dot-${i}`}
                    className={`lh-dot ${i === currentImgIndex ? "active" : ""}`}
                    onClick={() => setCurrentImgIndex(i)}
                  ></span>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="lh-card-main">
          <div className="lh-event-title">{event.title}</div>
          <div className="lh-corner-tl"></div>
          <div className="lh-corner-br"></div>
        </div>
      </div>
    </div>
  );
};

const LatestHappenings = () => {
  const [events, setEvents] = useState(fallbackEvents);

  useEffect(() => {
    let isMounted = true;

    const loadLatestEvents = async () => {
      let cachedEvents = null;
      try {
        const cachedValue = localStorage.getItem(CACHE_KEY);
        if (cachedValue) {
          const parsed = JSON.parse(cachedValue);
          if (
            parsed?.timestamp &&
            Date.now() - parsed.timestamp < CACHE_TTL_MS &&
            Array.isArray(parsed.events)
          ) {
            cachedEvents = parsed.events;
            setEvents(parsed.events);
          }
        }
      } catch (err) {
        // Ignore localStorage issues and continue to fetch fresh data.
      }

      const resolvedEvents = [];
      let anySuccess = false;

      for (const config of eventConfigs) {
        try {
          const response = await fetch(`${API_URL}${config.endpoint}`);
          if (!response.ok) throw new Error(`Failed to load ${config.endpoint}`);

          const payload = await response.json();
          const latestActivity = getLatestActivityFromPayload(payload);

          if (!latestActivity) {
            resolvedEvents.push({
              title: config.title,
              tag: config.tag,
              images: fallbackEvents[eventConfigs.indexOf(config)]?.images || [],
              placeholder: config.placeholder,
            });
            continue;
          }

          resolvedEvents.push({
            title: latestActivity.title || config.title,
            tag: config.tag,
            images: sanitizeImages(latestActivity),
            placeholder: config.placeholder,
            link: config.link,
          });
          anySuccess = true;
        } catch (error) {
          resolvedEvents.push({
            title: config.title,
            tag: config.tag,
            images: fallbackEvents[eventConfigs.indexOf(config)]?.images || [],
            placeholder: config.placeholder,
          });
        }
      }

      const finalEvents = anySuccess || !cachedEvents ? resolvedEvents : cachedEvents;

      if (isMounted) {
        setEvents(finalEvents);
        if (anySuccess) {
          localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({ events: resolvedEvents, timestamp: Date.now() })
          );
        }
      }
    };

    loadLatestEvents();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="lh-container">
      <div className="rs5865f2-titleWrap">
        <div className="rs5865f2-titleRow">
          <div className="rs5865f2-titlePlain">Our</div>
          <div className="rs5865f2-titleGradient">Events</div>
        </div>
        <div className="rs5865f2-gradientLine" />
      </div>

      <div className="lh-events-grid">
        {events.map((event, index) => (
          <EventCard key={`${event.title}-${index}`} event={event} />
        ))}
      </div>
    </div>
  );
};

export default LatestHappenings;